import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectDevicesAppsByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { expandGroupIdsWithDescendants } from "@dashboard/groups/utils";
import LicenseSelectors from "@dashboard/licenses/store/selectors";
import { Paper, TableContainer, TableRow, Theme, Tooltip, Typography } from "@mui/material";
import { CustomTileContainer, CustomTileTitle, HeaderContainer } from "@shared/CustomTile";
import { LoadingIcon } from "@shared/Loading";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import TablePagination from "@shared/table/TablePagination";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import TableItem from "./TableItem";

export interface TransformedApp {
    label: string;
    package: string;
    versionCode: number;
    versionName: string;
    count?: number;
}

const useStyles = makeStyles()((muiTheme: Theme) => ({
    container: { height: "100%" },
    table: { marginTop: muiTheme.spacing(3), boxShadow: "none" },
}));

const getUniqueKey = (app: TransformedApp) => `${app.package}-${app.versionCode}`;

const aggregateInstalledApps = (installedApps: TransformedApp[]) => {
    const appsMap = new Map<string, TransformedApp>();

    installedApps.forEach((app) => {
        const key = getUniqueKey(app);
        const existingApp = appsMap.get(key);
        if (existingApp) {
            existingApp.count = (existingApp.count || 0) + 1;
        } else {
            appsMap.set(key, { ...app, count: 1 });
        }
    });

    return Array.from(appsMap.values());
};

const columnHelper = createColumnHelper<TransformedApp>();

function getColumns() {
    return [
        columnHelper.accessor("label", {
            id: "label",
            header: i18next.t("pages:overviewPage.table.header.application"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.row.original.label}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("versionCode", {
            id: "versionCode",
            header: i18next.t("pages:overviewPage.table.header.version"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.getValue<string>()}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                const version = info.row.original.versionCode;
                const count = info.getValue<number>();
                return (
                    <TableItem
                        count={count}
                        version={version}
                        apps={info.row.original}
                        filteredGroupIds={info.table.options.meta?.filteredGroupIds || []}
                    />
                );
            },
            meta: {
                align: "right",
            },
        }),
    ];
}

type Props = {
    groupIds: number[];
};

export const ManagedAppsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const [sorting, setSorting] = useState<SortingState>([
        { id: "count", desc: true },
        { id: "label", desc: false },
    ]);
    const groups = useSelector(selectGroups);
    const filteredGroupIds = React.useMemo(() => {
        const base = !groupIds.length ? groups.map((group) => group.id) : groupIds;
        return expandGroupIdsWithDescendants(base, groups);
    }, [groupIds, groups]);

    const installedApps = useSelector((state) => selectDevicesAppsByGroupIds(state, filteredGroupIds), shallowEqual);
    const aggregatedApps = React.useMemo(() => aggregateInstalledApps(installedApps), [installedApps]);

    const licenseStatus = useSelector(LicenseSelectors.selectLicensesStatus);
    const isLoading = licenseStatus === "pending";

    const table = useReactTable<TransformedApp>({
        data: aggregatedApps,
        columns: getColumns(),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <CustomTileContainer className={classes.container}>
            <HeaderContainer>
                <CustomTileTitle>{t("pages:overviewPage.cards.managedApps.title")}</CustomTileTitle>
                {isLoading && (
                    <Tooltip title={t("pages:overviewPage.tooltips.loadingMoreData")} arrow>
                        <span>
                            <LoadingIcon warning />
                        </span>
                    </Tooltip>
                )}
            </HeaderContainer>
            <TableContainer component={Paper} className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody table={table} noDataMessage={t("pages:overviewPage.cards.managedApps.noApplications")}>
                        {table.getRowModel().rows.map((row) => (
                            <HoverRow<TransformedApp> key={row.id} {...row} clickable={false}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
                slotProps={{
                    actions: {
                        nextButton: { title: "" },
                        previousButton: { title: "" },
                    },
                }}
            />
        </CustomTileContainer>
    );
};
